import { Badge, Card, Image, Button } from "@oxygen/core";
import getThemeClasses from "@oxygen/theme";
import { CSSAbsolute } from "GlobalComponent/CSSCore/CSSAbsolute";
import { CSSFlex } from "GlobalComponent/CSSCore/CSSFlex";
import { CONFIDENCE, NA_PRICE, NoCarImage } from "GlobalConstant/index";
import { platform } from "GlobalEnum/Platform";
import { getCardImageSize } from "GlobalUtils/Common";
import { CustomLink } from "GlobalUtils/CustomLink";
import {
  formatUnveiledDate,
  getExpectedLaunchedDate,
  unveiledDateNeedTobeShown,
} from "GlobalUtils/DateTimeUtility";
import {
  getCardClasses,
  getDesktopCardClasses,
} from "GlobalUtils/ThemeClassUtils";
import { Track } from "GlobalUtils/Track";
import { getImageUrl } from "GlobalUtils/UrlFactory";
import PropTypes from "prop-types";
import React from "react";
import { getModelPageUrl } from "../../utils/UrlFactory";
import SubscribeLink from "./SubscribeLink";
import { propertyId } from "SrcPath/LeadForm/Enum/PropIdType";
import LeadFormPWA from "SrcPath/LeadForm/Containers/LeadFormPWA";

const propTypes = {
  /** content of the card. */
  data: PropTypes.shape({
    makeId: PropTypes.number.isRequired,
    makeName: PropTypes.string.isRequired,
    makeMaskingName: PropTypes.string.isRequired,
    modelId: PropTypes.number.isRequired,
    modelName: PropTypes.string.isRequired,
    modelMaskingName: PropTypes.string.isRequired,
    imagePath: PropTypes.string,
    status: PropTypes.number.isRequired,
    isSingleCard: PropTypes.bool,
    priceOverview: PropTypes.shape({
      formattedPrice: PropTypes.string,
      priceLabel: PropTypes.string,
      priceSuffix: PropTypes.string,
      pricePrefix: PropTypes.string,
      labelColor: PropTypes.string,
      reasonText: PropTypes.string,
      priceStatus: PropTypes.number,
      price: PropTypes.number,
      exShowRoomPrice: PropTypes.number,
    }),
    confidence: PropTypes.number.isRequired,
    launchedOn: PropTypes.string.isRequired,
    unveiledOn: PropTypes.string.isRequired,
  }),
  isSingleCard: PropTypes.bool,
  trackObj: PropTypes.object,
  showNotifyMe: PropTypes.bool,
};

const defaultProps = {
  platForm: platform.MOBILE,
  showNotifyMe: false,
};

const cardClasses = addBorder =>
  getThemeClasses(`
  height-full;
  bg-dimWhite md:bg-whiteSmokeLight;
  ${addBorder ? "border-width-1; border-style-solid; border-color-white;" : ""}
`);

const relativeClasses = getThemeClasses(`
  position-relative;
`);

function getCardBodyClasses(showNotifyMe) {
  return getThemeClasses(`
  flex-direction-column;
  height-full;
  pl-3;
  pr-3;
  pt-4;
  ${showNotifyMe ? "pb-3" : "pb-5"}
`);
}

const modelNameClasses = getThemeClasses(`
  font-size-8;
  color-charcoal;
  font-weight-regular;
`);

const priceClasses = getThemeClasses(`
  pt-2;
  font-size-8;
  font-weight-bold;
  color-charcoal;
`);

const priceLabelClasses = getThemeClasses(`
  ml-2;
  font-size-6.5;
  color-darkGray;
  font-weight-regular;
`);

const dateClasses = getThemeClasses(`
  mt-2;
  display-inline-block;
  font-size-6.5;
  color-dimGray;
`);

const cardContainerClasses = getThemeClasses(`
  height-full;
`);
const badgeContainerClasses = getThemeClasses(`
  left-2;
  top-2;
`);

const renderCTA = (leadFormAttributes, dealerAd) => {
  return (
    <LeadFormPWA
      attributes={leadFormAttributes}
      campaignDetail={dealerAd}
      dataTestingId="carousel-card-cta"
    >
      <Button color="secondary" variant="ghost" size="sm">
        Get Offers
      </Button>
    </LeadFormPWA>
  );
};

export const UpcomingCarCard = React.memo(function UpcomingCarCard(props) {
  const {
    data,
    isSingleCard,
    showBadge,
    testingId,
    trackObj = {},
    addBorder = false,
    showNotifyMe,
  } = props;

  if (!data) {
    return null;
  }
  let modelPageUrl = getModelPageUrl(
    data.makeMaskingName,
    data.modelMaskingName
  );
  // TODO: Fix this
  let getClasses = addBorder ? getDesktopCardClasses : getCardClasses;
  const { dealerAd, modelId, defaultVariant } = data;
  const leadFormAttributes = {
    modelId,
    versionId: defaultVariant,
    propId: propertyId.CAROUSEL_CARD_CTA,
    others: JSON.stringify({ CampaignCtaText: "Get Offers" }),
  };

  return (
    <Track trackImpression {...trackObj} className={cardContainerClasses}>
      <Card
        data-testing-id={testingId}
        {...getClasses(isSingleCard, cardClasses(addBorder))}
      >
        <Track
          as={CustomLink}
          to={modelPageUrl}
          title={`${data.makeName} ${data.modelName}`}
          trackClick
          {...trackObj}
          className={relativeClasses}
        >
          <Image
            src={
              data.imagePath
                ? getImageUrl({
                    imageSize: getCardImageSize(platform),
                    imageUrl: data.imagePath,
                  })
                : NoCarImage
            }
            description={`${data.makeName} ${data.modelName}`}
          />
          {showBadge && (
            <CSSAbsolute className={badgeContainerClasses}>
              <Badge color="brown" text="upcoming" textTransform="uppercase" />
            </CSSAbsolute>
          )}
        </Track>
        <CSSFlex className={getCardBodyClasses(showNotifyMe)}>
          <Track
            as={CustomLink}
            to={modelPageUrl}
            title={`${data.makeName} ${data.modelName}`}
            trackClick
            {...trackObj}
          >
            <h3
              className={modelNameClasses}
              data-lang-id="upcoming_card_car_name"
            >
              {`${data.makeName} ${data.modelName}`}
            </h3>
          </Track>
          <CSSFlex>
            <p className={priceClasses}>
              {data.priceOverview
                ? data.priceOverview.formattedPrice
                : NA_PRICE}
              {data.priceOverview && data.priceOverview.priceLabel && (
                <span className={priceLabelClasses}>
                  {data.priceOverview.priceLabel}
                </span>
              )}
            </p>
          </CSSFlex>
          {data.launchedOn && (
            <span
              className={dateClasses}
              data-lang-id="upcoming_card_launch_text"
            >
              {unveiledDateNeedTobeShown(data.launchedOn, data.unveiledOn)
                ? `Unveils on - ${formatUnveiledDate(
                    data.unveiledOn,
                    data.confidence == CONFIDENCE.HIGH
                  )}`
                : `When to  expect - ${getExpectedLaunchedDate({
                    launchedDate: data.launchedOn,
                    isHighConfidence: data.confidence == CONFIDENCE.HIGH,
                  })}`}
            </span>
          )}
          {dealerAd && renderCTA(leadFormAttributes, dealerAd)}
          <SubscribeLink
            showNotifyMe={showNotifyMe}
            modelId={data.modelId}
            makeName={data.makeName}
            modelName={data.modelName}
          />
        </CSSFlex>
      </Card>
    </Track>
  );
});

UpcomingCarCard.displayName = "UpcomingCarCard";
UpcomingCarCard.propTypes = propTypes;
UpcomingCarCard.defaultProps = defaultProps;
